<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const mapContainer = ref(null)
const { sourceGeojson, clusterGeojson, zoomLevel } = usePointCluster()
const { initMap, getRiverRiskData, setRiverRisk, updatePointIcon, loadedMap, tmpZoomLevel, tmpMapCenter } = useRiverMap()
const unClusteredGeojson = ref(null)
const mapZoom = useState('mapZoom', () => 4)
const mapCenter = useState('mapCenter', () => [137.0, 38.5])
const updateTime = ref('')
const isMapActivated = ref(false)

const onTouchMap = () => {
  if (!loadedMap.value) return
  if (!isMapActivated.value) {
    loadedMap.value.dragPan.enable()
    loadedMap.value.scrollZoom.enable()
    isMapActivated.value = true
  }
}

watch(() => clusterGeojson.value, (newValue) => {
  if (!loadedMap.value) return
  const map = loadedMap.value
  const source = map.getSource('obsCamClusteredPoints')
  source.setData(newValue)
})

watch(() => unClusteredGeojson.value, (newValue) => {
  if (!loadedMap.value) return
  const map = loadedMap.value
  const source = map.getSource('obsCamUnClusteredPoints')
  source.setData(newValue)
})

watch(() => tmpZoomLevel.value, (newValue) => {
  zoomLevel.value = newValue
  mapZoom.value = newValue
})

watch(() => tmpMapCenter.value, (newValue) => {
  mapCenter.value = newValue
})

onMounted(async () => {
  const [, riskData] = await Promise.all([
    initMap(mapContainer.value, mapZoom.value, mapCenter.value),
    getRiverRiskData()
  ])
  setRiverRisk(riskData)
  const [csv, waterLevel] = await Promise.all([
    $fetch('/api/river/csv'),
    $fetch('/api/river/waterlevel')
  ])
  const { clusteredGeoJson, unclusteredGeoJson } = makeGeoJson(csv, waterLevel.list)
  sourceGeojson.value = clusteredGeoJson
  unClusteredGeojson.value = unclusteredGeoJson
  updateTime.value = waterLevel.update_time
  updatePointIcon('no-select')
})
</script>

<template>
  <section class="zoomMap">
    <CommonPageTitle
      title="河川水位情報"
      :text="updateTime ? `${dayjs.unix(updateTime).tz('Asia/Tokyo').format('H:mm')}更新` : '--:--更新'"
    />
    <div
      ref="mapContainer"
      class="map-container"
      @touchend="onTouchMap()"
    >
      <RiverLegend />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.zoomMap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .map-container {
    position: relative;
    overflow: hidden;
    width: 100%;
    aspect-ratio: 75 / 68;
  }
}
:deep(.maplibregl-ctrl-group button) {
  width: 30px;
  height: 30px;
}
:deep(.maplibregl-ctrl-bottom-left) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  left: 0;
  bottom: 0;
  padding: 0 0 7px 7px;
  pointer-events: auto;
}
:deep(.maplibregl-ctrl-bottom-left > .maplibregl-ctrl) {
  margin: 0;
}
:deep(.maplibregl-ctrl-bottom-left .maplibregl-compact) {
  margin-inline: 3px;
}

@media screen and (min-width: 830px) {
  .zoomMap {
    .map-container {
      aspect-ratio: 151 / 100;
    }
  }
  :deep(.maplibregl-ctrl-group button) {
    width: 38px;
    height: 38px;
  }
  :deep(.maplibregl-ctrl-bottom-left .maplibregl-compact) {
    margin-inline: 7px;
  }
  :deep(.maplibregl-ctrl-bottom-left) {
    padding: 0 0 9px 9px;
  }
}
</style>
